import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui-kit/skeleton';

/**
 * Placeholders shown while a screen waits for its first API response, shaped like the content they
 * stand in for so nothing jumps when the data lands. Use them on the first load only (`isLoading`, no
 * data yet): a refetch keeps the current content on screen.
 */

const range = (count: number) => Array.from({ length: count }, (_, index) => index);

/** One short bar for a single value that arrives late (a name, a date, a status, a timer). */
export const InlineSkeleton = ({ className }: { className?: string }) => (
  <Skeleton className={cn('inline-block h-4 w-24 align-middle', className)} />
);

/** A page's title line, an optional subtitle and an optional action button on the right. */
export const PageHeaderSkeleton = ({
  subtitle = true,
  action = false,
  className,
}: {
  subtitle?: boolean;
  action?: boolean;
  className?: string;
}) => (
  <div className={cn('flex items-start justify-between gap-4', className)}>
    <div className="flex min-w-0 flex-1 flex-col gap-2">
      <Skeleton className="h-7 w-48 max-w-full" />
      {subtitle && <Skeleton className="h-4 w-64 max-w-full" />}
    </div>
    {action && <Skeleton className="h-9 w-28 shrink-0" />}
  </div>
);

/** Stat cards: the title above the value, or (on a phone) beside it, as the app's stat cards do. */
export const StatCardsSkeleton = ({
  count = 4,
  className,
}: {
  count?: number;
  className?: string;
}) => (
  <div className={cn('grid grid-cols-1 gap-4 max-md:gap-2 md:grid-cols-4', className)}>
    {range(count).map((index) => (
      <div
        key={index}
        className="flex flex-col gap-2 rounded-xl border bg-card p-4 max-md:flex-row max-md:items-center max-md:justify-between md:p-5"
      >
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-7 w-16" />
      </div>
    ))}
  </div>
);

/** Label/value pairs (detail pages, details dialogs, profile tabs). */
export const DetailRowsSkeleton = ({
  rows = 4,
  columns = 1,
  className,
}: {
  rows?: number;
  /** 1: label left, value right on one line; 2+: label above value, that many per line. */
  columns?: number;
  className?: string;
}) =>
  columns === 1 ? (
    <div className={cn('flex flex-col gap-3', className)}>
      {range(rows).map((index) => (
        <div key={index} className="flex items-center justify-between gap-4">
          <Skeleton className="h-4 w-28" />
          <Skeleton className="h-4 w-32" />
        </div>
      ))}
    </div>
  ) : (
    <div
      className={cn('grid gap-x-6 gap-y-4', className)}
      style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
    >
      {range(rows * columns).map((index) => (
        <div key={index} className="flex min-w-0 flex-col gap-1.5">
          <Skeleton className="h-3 w-20" />
          <Skeleton className="h-4 w-3/4" />
        </div>
      ))}
    </div>
  );

/** Form fields: a label over an input, one or more per line (on a phone always one per line). */
export const FormFieldsSkeleton = ({
  fields = 4,
  columns = 1,
  className,
}: {
  fields?: number;
  columns?: 1 | 2 | 3;
  className?: string;
}) => (
  <div
    className={cn(
      'grid grid-cols-1 gap-4',
      columns === 2 && 'md:grid-cols-2',
      columns === 3 && 'md:grid-cols-3',
      className
    )}
  >
    {range(fields).map((index) => (
      <div key={index} className="flex flex-col gap-2">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-9 w-full" />
      </div>
    ))}
  </div>
);

/** A card with a title and a few lines (a section, a chart, a breakdown). */
export const CardSkeleton = ({
  lines = 3,
  className,
  bodyClassName,
}: {
  lines?: number;
  className?: string;
  /** e.g. a fixed height for a chart area instead of lines. */
  bodyClassName?: string;
}) => (
  <div className={cn('flex flex-col gap-4 rounded-xl border bg-card p-5', className)}>
    <Skeleton className="h-5 w-40" />
    {bodyClassName ? (
      <Skeleton className={bodyClassName} />
    ) : (
      <div className="flex flex-col gap-2.5">
        {range(lines).map((index) => (
          <Skeleton key={index} className={cn('h-4', index % 3 === 2 ? 'w-2/3' : 'w-full')} />
        ))}
      </div>
    )}
  </div>
);

/** A page built around one form (an answer sheet, a form or survey editor): title, intro and fields. */
export const FormPageSkeleton = ({ fields = 5 }: { fields?: number }) => (
  <div className="flex w-full flex-col gap-4" aria-busy="true">
    <PageHeaderSkeleton />
    <div className="flex flex-col gap-6 rounded-xl border bg-card p-5">
      <FormFieldsSkeleton fields={fields} />
      <Skeleton className="h-9 w-28 self-end" />
    </div>
  </div>
);

/** Rows inside an open dropdown while its options load. */
export const OptionRowsSkeleton = ({ rows = 4 }: { rows?: number }) => (
  <div className="flex flex-col gap-1 p-1" aria-busy="true">
    {range(rows).map((index) => (
      <div key={index} className="px-2 py-1.5">
        <Skeleton className={cn('h-4', index % 2 ? 'w-2/3' : 'w-5/6')} />
      </div>
    ))}
  </div>
);

/** A select/filter trigger while the options it needs to show its value are still loading. */
export const SelectTriggerSkeleton = ({ className }: { className?: string }) => (
  <Skeleton className={cn('h-9 w-full', className)} />
);

/**
 * The app's frame (sidebar, top bar, a page title and content blocks) while the signed-in account,
 * its organization and its time zone are still loading, instead of a blank page with a spinner.
 */
export const AppShellSkeleton = () => (
  <div className="fixed inset-0 z-overlay flex bg-background" aria-busy="true">
    <aside className="hidden w-64 shrink-0 flex-col gap-6 border-r p-4 md:flex">
      <Skeleton className="h-8 w-32" />
      <div className="flex flex-col gap-3">
        {range(8).map((index) => (
          <div key={index} className="flex items-center gap-3">
            <Skeleton className="size-5 shrink-0 rounded" />
            <Skeleton className={cn('h-4', index % 3 === 1 ? 'w-24' : 'w-32')} />
          </div>
        ))}
      </div>
    </aside>
    <div className="flex min-w-0 flex-1 flex-col">
      <header className="flex h-14 items-center justify-between gap-4 border-b px-4">
        <Skeleton className="h-6 w-6 md:hidden" />
        <div className="ml-auto flex items-center gap-3">
          <Skeleton className="size-8 rounded-full" />
          <Skeleton className="size-8 rounded-full" />
        </div>
      </header>
      <main className="flex flex-col gap-4 p-4 md:p-6">
        <PageHeaderSkeleton action />
        <Skeleton className="h-10 w-full max-w-md" />
        <CardSkeleton lines={6} />
      </main>
    </div>
  </div>
);

/** A whole sign-in page (logo and card, centred) before the auth layout can render. */
export const AuthPageSkeleton = () => (
  <div className="fixed inset-0 z-overlay flex items-center justify-center bg-background p-6">
    <div className="flex w-full max-w-[28rem] flex-col items-center gap-8">
      <Skeleton className="h-12 w-40" />
      <AuthCardSkeleton />
    </div>
  </div>
);

/** The sign-in card while the project's login options load. */
export const AuthCardSkeleton = () => (
  <div className="flex w-full flex-col gap-6" aria-busy="true">
    <div className="flex flex-col gap-2">
      <Skeleton className="h-8 w-40" />
      <Skeleton className="h-4 w-64 max-w-full" />
    </div>
    <FormFieldsSkeleton fields={2} />
    <Skeleton className="h-10 w-full" />
  </div>
);
